import { Button, Divider, IconButton, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { Control, Controller, ControllerRenderProps, FormState, useFieldArray } from "react-hook-form";
import {
  Autocomplete,
  DraggableList,
  EditAgendaItemModal,
  ICreateEditAgendaItemFormState,
  SmallInputLabel,
} from "../../../../components";
import { MeetingAgendaItemDto, useGetUsers } from "../../../../http";
import { IUser } from "../../../../models";
import { lexorank } from "../../../../utilities";
import { v4 as newGuid } from "uuid";
import { useState } from "react";
import { EditOutlined } from "@mui/icons-material";
import { MeetingType } from "../../../../constants";

interface ICreateEditTeamMeetingFormState {
  title: string;
  description: string;
  externalCallUrl: string;
  invitees: IUser[];
  agendaItems: MeetingAgendaItemDto[];
}

interface IProps {
  formState: FormState<ICreateEditTeamMeetingFormState>;
  control: Control<ICreateEditTeamMeetingFormState, any>;
  loadingEnabled: boolean;
}

const CreateEditTeamMeetingForm = (props: IProps) => {
  const {
    formState: { errors },
    control,
    loadingEnabled,
  } = props;
  const { isLoading: isLoadingUsers, data: users } = useGetUsers(loadingEnabled);

  const {
    fields: agendaItems,
    append: addAgendaItem,
    update: updateAgendaItem,
    remove: removeAgendaItem,
  } = useFieldArray({
    control,
    name: "agendaItems",
  });

  const [selectedAgendaItemGuid, setSelectedAgendaItemGuid] = useState<string | null>(null);
  const [isAgendaItemModalVisible, setIsAgendaItemModalVisible] = useState(false);

  function handleAgendaItemSubmit(value: ICreateEditAgendaItemFormState) {
    const existingItem = agendaItems.find((item) => item.guid === selectedAgendaItemGuid);

    if (existingItem === undefined) {
      addAgendaItem({ ...value, guid: newGuid(), sortOrder: lexorank.getNextRank(agendaItems) });
    } else {
      updateAgendaItem(
        agendaItems.findIndex((item) => item.guid === selectedAgendaItemGuid),
        {
          ...existingItem,
          ...value,
        },
      );
    }

    setSelectedAgendaItemGuid(null);
    setIsAgendaItemModalVisible(false);
  }

  function handleAgendaItemReordered(
    field: ControllerRenderProps<ICreateEditTeamMeetingFormState, "agendaItems">,
    sourceIndex: number,
    destinationIndex: number,
  ) {
    var reorderedValues = field.value.map((value, index) => {
      if (index === sourceIndex) {
        return {
          ...value,
          sortOrder: lexorank.getRank(field.value, sourceIndex, destinationIndex),
        };
      }

      return value;
    });

    field.onChange(reorderedValues);
  }

  return (
    <Stack spacing={2}>
      <Stack spacing={0.5}>
        <Stack spacing={0.2} sx={{ px: 1 }}>
          <Typography variant="body2">Basic Settings</Typography>
          <Divider />
        </Stack>

        <Stack spacing={0.2} sx={{ px: 1 }}>
          <SmallInputLabel>Title *</SmallInputLabel>
          <Controller
            name="title"
            control={control}
            rules={{ required: { message: "Title is required", value: true } }}
            render={({ field }) => (
              <TextField
                {...field}
                error={errors["title"] !== undefined}
                helperText={errors["title"]?.message}
                placeholder="Enter a title for the meeting..."
                inputProps={{ maxLength: 64 }}
              />
            )}
          />
        </Stack>
        <Stack spacing={0.2} sx={{ px: 1 }}>
          <SmallInputLabel>Description *</SmallInputLabel>
          <Controller
            name="description"
            control={control}
            rules={{ required: { message: "Description is required", value: true } }}
            render={({ field }) => (
              <TextField
                {...field}
                error={errors["description"] !== undefined}
                helperText={errors["description"]?.message}
                placeholder="Describe the purpose of this meeting..."
                multiline
              />
            )}
          />
        </Stack>
        <Stack spacing={0.2} sx={{ px: 1 }}>
          <SmallInputLabel>Video Call Link</SmallInputLabel>
          <Controller
            name="externalCallUrl"
            control={control}
            render={({ field }) => (
              <TextField {...field} placeholder="Provide a link to your video call (Teams, Zoom, etc.)" />
            )}
          />
        </Stack>
      </Stack>

      <Stack spacing={0.5}>
        <Stack spacing={0.2} sx={{ px: 1 }}>
          <Typography variant="body2">Participation</Typography>
          <Divider />
          <Typography>
            Aside from your team members you can give additional guest participants access to your meeting.
          </Typography>
        </Stack>
        <Stack spacing={0.2} sx={{ px: 1 }}>
          <Controller
            name="invitees"
            control={control}
            render={({ field }) => (
              <Autocomplete
                isLoading={isLoadingUsers}
                disabled={isLoadingUsers}
                options={users ?? []}
                getOptionLabel={(option) => option.displayName}
                renderOption={(props, option) => (
                  <MenuItem component="li" {...props} key={option.userId}>
                    <Stack>
                      <Typography>{option.displayName}</Typography>
                      <Typography variant="caption">{option.email}</Typography>
                    </Stack>
                  </MenuItem>
                )}
                onChange={(_, option) => field.onChange(option)}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select people..." inputProps={{ ...params.inputProps }} />
                )}
                value={field.value}
                isOptionEqualToValue={(option, value) => option.userId === value.userId}
                multiple
              />
            )}
          />
        </Stack>
      </Stack>

      <Stack>
        <Stack spacing={0.25} sx={{ px: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="body2">Agenda</Typography>
            <Button variant="tertiary" onClick={() => setIsAgendaItemModalVisible(true)}>
              Add Agenda Step
            </Button>
          </Stack>
          <Divider />
        </Stack>

        <Controller
          name="agendaItems"
          control={control}
          render={({ field }) => (
            <DraggableList
              id={field.name}
              dataSource={field.value.sort((a, b) => a.sortOrder.localeCompare(b.sortOrder))}
              confirmDeletion
              renderItem={(item) => (
                <Stack
                  direction="row"
                  key={item.guid}
                  sx={{
                    alignItems: "center",
                    justifyContent: "space-between",
                    overflow: "hidden",
                  }}
                >
                  <Stack overflow="hidden">
                    <Typography>{item.name}</Typography>
                    <Typography variant="caption" noWrap overflow="hidden" textOverflow="ellipsis">
                      {item.description}
                    </Typography>
                  </Stack>

                  <IconButton
                    size="small"
                    onClick={() => {
                      setSelectedAgendaItemGuid(item.guid);
                      setIsAgendaItemModalVisible(true);
                    }}
                  >
                    <EditOutlined />
                  </IconButton>
                </Stack>
              )}
              renderEmptyPlaceholder={() => (
                <Typography variant="body1" sx={{ px: 1, pt: 0.25 }}>
                  Nothing here yet.
                </Typography>
              )}
              onReorder={(sourceIndex, destinationIndex) =>
                handleAgendaItemReordered(field, sourceIndex, destinationIndex)
              }
              onDelete={(index) => removeAgendaItem(index)}
            />
          )}
        />
      </Stack>
      <EditAgendaItemModal
        visible={isAgendaItemModalVisible}
        defaultValues={
          selectedAgendaItemGuid ? agendaItems.find((item) => item.guid === selectedAgendaItemGuid) : undefined
        }
        meetingType={MeetingType.Team}
        onSubmit={handleAgendaItemSubmit}
        onCancel={() => {
          setIsAgendaItemModalVisible(false);
          setSelectedAgendaItemGuid(null);
        }}
      />
    </Stack>
  );
};

export { CreateEditTeamMeetingForm };
export type { ICreateEditTeamMeetingFormState };
